import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { servicesApi } from "../services/api";

function ConsultingFocusAreaDetailPage() {
  const { id } = useParams();
  const [area, setArea] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchArea();
  }, [id]);

  const fetchArea = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await servicesApi.getConsultingFocusArea(id);
      setArea(response.data);
    } catch (err) {
      console.error("Error loading consulting focus area:", err);
      setError("Focus area not found");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  if (error || !area) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="bg-red-50 border border-red-200 rounded-lg p-8 text-center">
          <h1 className="text-2xl font-bold text-red-800 mb-4">
            Focus Area Not Found
          </h1>
          <p className="text-red-600 mb-6">
            {error || "The focus area you are looking for does not exist."}
          </p>
          <Link
            to="/consulting"
            className="inline-block bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition"
          >
            Back to Consulting
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-[#1F3A5F] to-[#3B82F6] text-white py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-6">
            <Link
              to="/consulting"
              className="text-blue-100 hover:text-white transition"
            >
              ← Back to Consulting
            </Link>
          </div>
          <div className="flex items-start gap-6">
            {area.icon && <div className="text-6xl">{area.icon}</div>}
            <div>
              <h1 className="text-4xl md:text-5xl font-bold mb-4">
                {area.title}
              </h1>
              <p className="text-blue-100 text-xl">{area.description}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Expertise Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-8">Our Approach</h2>
          <div className="prose prose-lg max-w-none">
            <p>{area.description}</p>
            <p>
              We leverage data-driven insights, industry best practices, and
              hands-on experience to deliver customized solutions tailored to
              your business needs and goals.
            </p>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 bg-blue-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-12 text-center">Benefits</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white rounded-lg shadow p-8">
              <div className="text-4xl text-blue-600 mb-4">📊</div>
              <h3 className="text-xl font-bold mb-3">Data-Driven Strategy</h3>
              <p className="text-gray-600">
                Decisions backed by comprehensive market analysis and actionable
                insights.
              </p>
            </div>
            <div className="bg-white rounded-lg shadow p-8">
              <div className="text-4xl text-blue-600 mb-4">⚡</div>
              <h3 className="text-xl font-bold mb-3">Fast Implementation</h3>
              <p className="text-gray-600">
                Quick turnaround time with measurable milestones and clear
                deliverables.
              </p>
            </div>
            <div className="bg-white rounded-lg shadow p-8">
              <div className="text-4xl text-blue-600 mb-4">🎯</div>
              <h3 className="text-xl font-bold mb-3">Measurable Results</h3>
              <p className="text-gray-600">
                Focus on KPIs and continuous optimization to maximize ROI.
              </p>
            </div>
            <div className="bg-white rounded-lg shadow p-8">
              <div className="text-4xl text-blue-600 mb-4">🤝</div>
              <h3 className="text-xl font-bold mb-3">Expert Partnership</h3>
              <p className="text-gray-600">
                Dedicated support from experienced consultants throughout your
                journey.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-blue-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">
            Ready to Get Consulting Support?
          </h2>
          <p className="mb-8 text-blue-100 text-lg">
            Let's discuss how we can help you achieve your business goals.
          </p>
          <Link
            to="/contact"
            className="inline-block bg-white text-blue-600 px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition"
          >
            Book a Strategy Session
          </Link>
        </div>
      </section>

      {/* Back to Consulting */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Link
            to="/consulting"
            className="text-blue-600 font-semibold hover:text-blue-700 transition"
          >
            ← View All Focus Areas
          </Link>
        </div>
      </section>
    </div>
  );
}

export default ConsultingFocusAreaDetailPage;
